/**
 * Product Media Slider Component
 * Handles swipeable product images on collection pages
 * Uses Swiper for smooth sliding interactions
 */

import { Component } from "@theme/component-base";

export class ProductMediaSlider extends Component {
  connectedCallback() {
    // Wait for Swiper to be available
    if (typeof window.Swiper === 'undefined') {
      setTimeout(() => this.connectedCallback(), 100);
      return;
    }

    const parentSliderBlock = this.closest('[data-slider-block]');
    if (parentSliderBlock && !parentSliderBlock.querySelector('.swiper-initialized')) {
      this.#waitForParentSliderBlock(parentSliderBlock);
      return;
    }

    this.#setupSwatchListener();
    this.#initSwiper();
  }

  #waitForParentSliderBlock(parentSliderBlock) {
    let attempts = 0;

    const tryInit = () => {
      if (!this.isConnected) return;

      if (parentSliderBlock.querySelector('.swiper-initialized')) {
        this.#setupSwatchListener();
        this.#initSwiper();
        return;
      }

      attempts += 1;
      if (attempts > 200) {
        this.#setupSwatchListener();
        this.#initSwiper();
        return;
      }

      requestAnimationFrame(tryInit);
    };

    requestAnimationFrame(tryInit);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Destroy Swiper instance
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  #setupSwatchListener() {
    // Find parent swatches component
    const swatchComponent = this.closest('[data-product-card]')?.querySelector('swatches-item-component');
    
    if (swatchComponent) {
      swatchComponent.addEventListener('swatch:change', (e) => {
        this.#onSwatchChange(e.detail.mediaId);
      });
    }
  }
  
  #onSwatchChange(mediaId) {    
    if (!this.swiper || !mediaId) {
      console.warn('[Slider] Missing swiper or mediaId');
      return;
    }
    
    // Find slide index with matching media ID
    const slides = this.querySelectorAll('.swiper-slide[data-media-id]');
    
    const slideIndex = Array.from(slides).findIndex(
      slide => slide.dataset.mediaId === mediaId
    );
    
    if (slideIndex !== -1) {
      this.swiper.slideTo(slideIndex);
    } else {
      console.warn('[Slider] No matching slide found for mediaId:', mediaId);
    }
  }

  /**
   * Initialize Swiper slider
   */
  #initSwiper() {
    const wrapper = this.querySelector('.swiper');
    
    if (!wrapper) {
      console.warn('[ProductMediaSlider] No .swiper wrapper found');
      return;
    }

    // Check if there are multiple slides
    const slides = wrapper.querySelectorAll('.swiper-slide');
    
    if (slides.length <= 1) {
      return; // No need for slider with only one image
    }
    
    try {
      this.swiper = new window.Swiper(wrapper, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        navigation: {
          nextEl: wrapper.querySelector('.product-card-controls-left'),
          prevEl: wrapper.querySelector('.product-card-controls-right'),
        },
        // Prevent click events from bubbling to parent links
        touchStartPreventDefault: false,
        preventClicks: false,
        preventClicksPropagation: false,
      });
    } catch (error) {
      console.error('[ProductMediaSlider] Failed to initialize:', error);
    }
  }
}

customElements.define("product-media-slider", ProductMediaSlider);
